import React from 'react';
import { useIntl } from 'react-intl';

import { CardSelect } from '~/eds/src/components/CardSelect/CardSelect';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';

import {
  EsignatureIntegrationType,
  EsignatureIntegrationWithMetadata,
} from '../types';

interface ESignatureAuthenticationOptionsProps {
  disabled?: boolean;
  providerName?: string; // E.g. Docusign, Adobe Sign
  integration?: EsignatureIntegrationWithMetadata;
  onChange?: (value?: EsignatureIntegrationType) => void;
}

export const ESignatureAuthenticationOptions = ({
  disabled = false,
  providerName,
  integration,
  onChange = () => {},
}: ESignatureAuthenticationOptionsProps) => {
  const intl = useIntl();

  const { isActive, integrationType: type } = integration || {};
  const selectedIntegrationType = type || 'company';

  function handleChange(
    updatedValue?: EsignatureIntegrationType | string | null,
  ): void {
    if (!updatedValue) return;

    onChange(updatedValue as EsignatureIntegrationType);
  }

  return (
    <Section
      title={intl.formatMessage({
        id: 'NewESignature.ESignatureIntegrationCard.AuthenticationCardTitle',
        defaultMessage: 'E-signature authentication',
        description: 'Title for the e-signature authentication selection card',
      })}
      description={intl.formatMessage({
        id: 'NewESignature.ESignatureIntegrationCard.AuthenticationCardDescription',
        defaultMessage:
          'Select whether users collect signatures with the Company Personal account.',
        description:
          'Description explaining e-signature authentication options',
      })}
      disabled={disabled}
    >
      <Layout
        direction="column"
        spacing={0}
        styles={{ maxWidth: '550px', width: '100%' }}
      >
        <CardSelect
          direction="column"
          spacing="s"
          value={selectedIntegrationType}
          disabled={disabled}
          onChange={handleChange}
          isMulti={false}
          name="Authentication Options"
          options={[
            {
              label: intl.formatMessage({
                id: 'NewESignature.ESignatureIntegrationCard.CompanyAccountLabel',
                defaultMessage: 'Company account (Recommended)',
                description: 'Label for company account authentication option',
              }),
              description: intl.formatMessage(
                {
                  id: 'NewESignature.ESignatureIntegrationCard.CompanyAccountDescription',
                  defaultMessage:
                    'The Administrator authenticates with {providerName} once. Users can send documents for signature without authenticating. Signers will receive emails from one authenticated account.',
                  description:
                    'Description of how company account authentication works for e-signature integration. Example: providerName = "DocuSign"',
                },
                { providerName },
              ),
              value: 'company',
              isActive: isActive && type === 'company',
            },
            {
              label: intl.formatMessage({
                id: 'NewESignature.ESignatureIntegrationCard.PersonalAccountLabel',
                defaultMessage: 'Personal account',
                description: 'Label for personal account authentication option',
              }),
              description: intl.formatMessage(
                {
                  id: 'NewESignature.ESignatureIntegrationCard.PersonalAccountDescription',
                  defaultMessage:
                    "Each user authenticates with {providerName} during the Sign stage of a Workflow Ticket. Signers will receive emails from the user's authenticated account.",
                  description:
                    'Description of how personal account authentication works for e-signature integration. Example: providerName = "DocuSign"',
                },
                { providerName },
              ),
              value: 'personal',
              isActive: isActive && type === 'personal',
            },
          ]}
        />
      </Layout>
    </Section>
  );
};
